import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Crown, LogOut, UserPlus, Trophy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { getTeam, inviteToTeam, leaveTeam, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";

function InviteDialog({ teamId, onInvited }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ target_user_id: "", email: "", message: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.target_user_id && !form.email) {
      toast.error("Provide a user ID or email.");
      return;
    }
    setSaving(true);
    try {
      const payload = { message: form.message };
      if (form.target_user_id) payload.target_user_id = form.target_user_id;
      if (form.email) payload.email = form.email;
      await inviteToTeam(teamId, payload);
      toast.success("Invite sent.");
      onInvited();
      setOpen(false);
      setForm({ target_user_id: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="invite-btn"
          className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
          <UserPlus size={14} className="mr-2" /> Invite Player
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-[#D4AF37]/30 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Invite Player</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">User ID (existing TGL account)</Label>
            <Input data-testid="invite-userid-input" value={form.target_user_id}
              onChange={(e) => setForm({ ...form, target_user_id: e.target.value })}
              placeholder="user_xxxxxxxxxxxx"
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <div className="text-center text-xs uppercase tracking-widest text-zinc-500">— or —</div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
            <Input type="email" data-testid="invite-email-input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Message (optional)</Label>
            <Input data-testid="invite-message-input" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="invite-submit"
              className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
              {saving ? "Sending..." : "Send Invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaving, setLeaving] = useState(false);

  const reload = () => {
    setLoading(true);
    getTeam(teamId).then(setTeam).catch(() => setTeam(null)).finally(() => setLoading(false));
  };

  useEffect(reload, [teamId]);

  const isCaptain = team && user && team.captain_id === user.user_id;
  const isMember = team && user && team.member_ids?.includes(user.user_id);

  const handleLeave = async () => {
    if (!confirm("Leave this team?")) return;
    setLeaving(true);
    try {
      await leaveTeam(teamId);
      toast.success("You left the team.");
      await refresh();
      navigate("/teams");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLeaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] text-zinc-400 grid place-items-center">Loading...</div>;
  if (!team) return <div className="min-h-screen bg-[#050505] text-zinc-400 grid place-items-center">Team not found.</div>;

  const stats = team.stats || {};

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-20 px-6">
      <Navbar />
      <div className="max-w-5xl mx-auto">
        <Link to="/teams" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#D4AF37] inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={12} /> All Teams
        </Link>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">[{team.tag}]</div>
            <h1 className="font-display text-4xl lg:text-5xl font-black uppercase text-white" data-testid="team-name">
              {team.name}
            </h1>
            {team.bio && <p className="mt-2 text-zinc-400 max-w-xl">{team.bio}</p>}
          </div>
          <div className="flex items-center gap-2">
            {isCaptain && <InviteDialog teamId={team.id} onInvited={reload} />}
            {isMember && (
              <Button onClick={handleLeave} disabled={leaving} variant="outline"
                data-testid="leave-team-btn"
                className="border-white/15 text-zinc-300 hover:border-red-500/40 hover:text-red-300">
                <LogOut size={14} className="mr-2" /> {leaving ? "Leaving..." : "Leave Team"}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="tgl-card cut-corners p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Tournaments</div>
            <div className="font-display text-xl font-black gold-text">{stats.tournaments || 0}</div>
          </div>
          <div className="tgl-card cut-corners p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Wins</div>
            <div className="font-display text-xl font-black gold-text">{stats.wins || 0}</div>
          </div>
          <div className="tgl-card cut-corners p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Matches Won</div>
            <div className="font-display text-xl font-black gold-text">{stats.matches_won || 0}</div>
          </div>
          <div className="tgl-card cut-corners p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Prize Earned</div>
            <div className="font-display text-xl font-black gold-text">${stats.prize_total || 0}</div>
          </div>
        </div>

        <div className="tgl-card cut-corners p-6">
          <h3 className="font-display uppercase text-lg mb-4">Roster</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(team.members || []).map((m) => (
              <div key={m.user_id} data-testid={`member-${m.user_id}`}
                className="border border-white/10 cut-corners p-4 flex items-center gap-3 bg-black/40">
                <div className="w-12 h-12 rounded-full overflow-hidden ring-1 ring-[#D4AF37]/40 bg-black grid place-items-center shrink-0">
                  {m.avatar_url ? (
                    <img src={m.avatar_url} alt={m.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-display text-lg gold-text">
                      {(m.gamertag || m.display_name || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1 font-display uppercase truncate">
                    {m.is_captain && <Crown size={14} className="text-[#D4AF37] shrink-0" />}
                    {m.display_name}
                  </div>
                  <div className="text-xs text-zinc-400 truncate">@{m.gamertag || "-"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {isCaptain && (
          <div className="mt-6 border border-[#D4AF37]/20 cut-corners p-4 bg-black/40 flex items-start gap-3">
            <Trophy size={18} className="text-[#D4AF37] mt-0.5" />
            <div className="text-sm text-zinc-300">
              As captain, only you can register this team for tournaments. Head to the{" "}
              <Link to="/#tournaments" className="text-[#D4AF37] hover:underline">Tournaments section</Link> to enlist.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
